import React, { ChangeEvent, useEffect, useRef } from 'react';
import { Drawer, Button, Input, InputNumber, Form, Image, Select } from 'antd';
import { CloseOutlined, FileImageOutlined } from '@ant-design/icons';
import {
	uploadPhotos,
	uploadCover,
	removePhoto,
	changeLot,
	closeModal,
	getLotById,
	removeCover,
	EditLotModalSelector,
} from './EditLotModalSlice';
import * as S from './EditLotModal.styled';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import {
	ICategory,
	IComplex,
	IConsultation,
	IRegion,
	IFavourite,
	IFurnishingStatus,
	ILotPhoto,
	IPurpose,
	IRentFrequency,
	IType,
} from 'interfaces/main';
import { LibrariesSelector } from 'features/Libraries/LibrariesSlice';
import { exchangeLot } from 'features/LotsList/LotsListSlice';
import { unwrapResult } from '@reduxjs/toolkit';
// import { OrganizationsListItemsSelector } from 'features/OrganizationsList/OrganizationsListSlice';
import { ComplexesListItemsSelector } from 'features/ComplexesList/ComplexesListSlice';
import { JSX } from 'react/jsx-runtime';
import { IsOwnerSelector } from 'features/User/UserSlice';
import Spinner from 'components/Spinner';

const layout = {
	labelCol: { span: 6 },
	wrapperCol: { span: 18 },
};

const { Option } = Select;

const EditLotModal = (): JSX.Element => {
	const dispatch = useAppDispatch();

	const [form] = Form.useForm();

	const photosInputRef = useRef<HTMLInputElement | null>(null);
	const coverInputRef = useRef<HTMLInputElement | null>(null);

	// Данные организации
	const { data, isVisible, isCoverLoading, isPhotosLoading, isLoading } =
		useAppSelector(EditLotModalSelector);

	// Доступные организации
	// const allOrganizations = useAppSelector(OrganizationsListItemsSelector);

	// Доступные ЖК
	const allComplexes = useAppSelector(ComplexesListItemsSelector);

	// Суперадмин
	const isOwner = useAppSelector(IsOwnerSelector);

	// Словари
	const {
		furnishingStatuses: allFurnishingStatuses,
		completedStatuses: allCompletedStatuses,
		rentFrequencies: allRentFrequencies,
		types: allTypes,
		purposes: allPurposes,
		categories: allCategories,
		regions: allRegions,
	} = useAppSelector(LibrariesSelector);

	const {
		lotCover,
		id,
		unit,
		totalArea,
		suiteArea,
		balconyArea,
		plotArea,
		baths,
		rooms,
		floor,
		price,
		isArchived,
		purposeId,
		furnishingStatusId,
		completedStatusId,
		typeId,
		rentFrequencyId,
		complexId,
		categoryId,
		favourites,
		consultations,
		lotPhotos,
	} = data;

	// Получение данных организации
	useEffect(() => {
		if (id)
			dispatch(getLotById({ id }))
				.then(unwrapResult)
				.then((fetchedLot) => {
					const l = {
						...fetchedLot,
						favourites: fetchedLot.favourites.map((item: IFavourite) => ({
							key: item.id,
							value: `userId: ${item.userId}`,
						})),
						consultations: fetchedLot.consultations.map((item: IConsultation) => ({
							key: item.id,
							value: `userId: ${item.userId}`,
						})),
					};

					const fields = Object.keys(l).map((fieldName: string) => ({
						name: fieldName,
						value: l[fieldName],
					}));

					form.setFields(fields);
				});
	}, [dispatch, form, id]);

	// Закрытие окна
	const onClose = () => {
		form.submit();
	};

	// Загрузка новых фотографий
	const onUploadPhotos = (event: ChangeEvent<HTMLInputElement>) => {
		let newFileList = [...event.target.files];

		if (!!newFileList.length) {
			dispatch(uploadPhotos({ files: newFileList, lotId: id }));
		}
	};

	// Загрузка обложка
	const onUploadCover = (event: ChangeEvent<HTMLInputElement>) => {
		let newFile = event.target.files[0];

		if (!!newFile) {
			dispatch(uploadCover({ file: newFile, lotId: id }));
		}
	};

	const onUploadPhotosClick = () => {
		photosInputRef.current?.click();
	};

	const onUploadCoverClick = () => {
		coverInputRef.current?.click();
	};

	const onRemovePhoto = (id: number) => {
		dispatch(removePhoto({ id }));
	};

	const onSubmit = () => {
		dispatch(exchangeLot(data));
		dispatch(closeModal());
	};

	const onRemoveCover = () => {
		dispatch(removeCover(lotCover.id));
	};

	// Ошибки в форме при сабмите
	const onError = (errors) => console.log(errors);

	// Изменение основных полей организации
	const onChangeLot = (key: string, value: string | number | boolean) =>
		dispatch(changeLot({ key, lotId: id, value }));

	// Изменение текстового поля
	const onChangeTextField = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
		onChangeLot(event.target.name, event.target.value);

	// Изменение числового поля
	const onChangeNumberField = (name: string, value: number) => onChangeLot(name, value);

	// Изменение логического поля
	const onChangeBooleanField = (name: string, value: boolean) => onChangeLot(name, value);

	return (
		<Drawer title="Edit lot" placement="right" width={624} onClose={onClose} open={isVisible}>
			{(isCoverLoading || isCoverLoading || isLoading) && <Spinner />}
			<Form
				{...layout}
				name="editLotForm"
				id="editLotForm"
				form={form}
				initialValues={{
					unit,
					totalArea,
					suiteArea,
					balconyArea,
					plotArea,
					baths,
					rooms,
					floor,
					price,
					isArchived,
					purposeId,
					categoryId,
					furnishingStatusId,
					completedStatusId,
					typeId,
					rentFrequencyId,
					complexId,
					favourites: favourites.map((item: IFavourite) => ({
						key: item.id,
						value: `userId: ${item.userId}`,
					})),
					consultations: consultations.map((item: IConsultation) => ({
						key: item.id,
						value: `userId: ${item.userId}`,
					})),
				}}
				onFinish={onSubmit}
				onError={onError}>
				<Form.Item name="unit" label="Unit">
					<Input type="text" placeholder="105" name="unit" onChange={onChangeTextField} />
				</Form.Item>
				<Form.Item name="floor" label="Floor">
					<InputNumber
						type="text"
						name="floor"
						placeholder="1"
						style={{ width: '100%' }}
						min={0}
						max={50}
						step={1}
						onChange={(value: number) => onChangeNumberField('floor', value)}
					/>
				</Form.Item>
				<Form.Item
					name="totalArea"
					label="Total area"
					rules={[{ required: true, message: 'Please input total area' }]}>
					<InputNumber
						type="text"
						name="totalArea"
						placeholder="1000"
						style={{ width: '100%' }}
						onChange={(value: number) => onChangeNumberField('totalArea', value)}
					/>
				</Form.Item>
				<Form.Item name="suiteArea" label="Suite area">
					<InputNumber
						type="text"
						name="suiteArea"
						placeholder="1000"
						style={{ width: '100%' }}
						onChange={(value: number) => onChangeNumberField('suiteArea', value)}
					/>
				</Form.Item>
				<Form.Item name="balconyArea" label="Balcony area">
					<InputNumber
						type="text"
						name="balconyArea"
						placeholder="1000"
						style={{ width: '100%' }}
						onChange={(value: number) => onChangeNumberField('balconyArea', value)}
					/>
				</Form.Item>
				<Form.Item name="plotArea" label="Plot area">
					<InputNumber
						type="text"
						name="plotArea"
						placeholder="1000"
						style={{ width: '100%' }}
						onChange={(value: number) => onChangeNumberField('plotArea', value)}
					/>
				</Form.Item>
				<Form.Item name="baths" label="Baths">
					<InputNumber
						type="text"
						name="baths"
						placeholder="1"
						min={1}
						max={6}
						step={1}
						style={{ width: '100%' }}
						onChange={(value: number) => onChangeNumberField('baths', value)}
					/>
				</Form.Item>
				<Form.Item name="rooms" label="Bedrooms">
					<InputNumber
						type="text"
						name="rooms"
						placeholder="1"
						style={{ width: '100%' }}
						min={0}
						max={6}
						step={1}
						onChange={(value: number) => onChangeNumberField('rooms', value)}
					/>
				</Form.Item>
				<Form.Item name="planType" label="Plan type">
					<Input type="text" placeholder="B" name="planType" onChange={onChangeTextField} />
				</Form.Item>
				<Form.Item name="price" label="Price">
					<InputNumber
						type="text"
						name="price"
						placeholder="100000"
						style={{ width: '100%' }}
						onChange={(value: number) => onChangeNumberField('price', value)}
					/>
				</Form.Item>
				<Form.Item
					name="regionId"
					label="Region"
					rules={[{ required: true, message: 'Please select Region' }]}>
					<Select
						style={{ width: '100%' }}
						placeholder="Please select Region"
						onChange={(value: number) => onChangeNumberField('regionId', value)}>
						{allRegions.map((item: IRegion) => (
							<Option key={item.id} value={item.id}>
								{item.name_en}
							</Option>
						))}
					</Select>
				</Form.Item>
				<Form.Item
					name="categoryId"
					label="Category"
					rules={[{ required: true, message: 'Please select category' }]}>
					<Select
						style={{ width: '100%' }}
						placeholder="Please select category"
						onChange={(value: number) => onChangeNumberField('categoryId', value)}>
						{allCategories.map((item: ICategory) => (
							<Option key={item.id} value={item.id}>
								{item.title_en}
							</Option>
						))}
					</Select>
				</Form.Item>
				<Form.Item name="isArchived" label="Is archived">
					<Select
						style={{ width: '100%' }}
						placeholder="Please select"
						onChange={(value: boolean) => onChangeBooleanField('isArchived', value)}>
						<Option value={true}>Yes</Option>
						<Option value={false}>No</Option>
					</Select>
				</Form.Item>
				<Form.Item name="purposeId" label="Purpose">
					<Select
						style={{ width: '100%' }}
						placeholder="Please select"
						onChange={(value: number) => onChangeNumberField('purposeId', value)}>
						{allPurposes.map((item: IPurpose) => (
							<Option key={item.id} value={item.id}>
								{item.name_en}
							</Option>
						))}
					</Select>
				</Form.Item>
				<Form.Item name="furnishingStatusId" label="Furnishing status">
					<Select
						style={{ width: '100%' }}
						placeholder="Please select"
						onChange={(value: number) => onChangeNumberField('furnishingStatusId', value)}>
						{allFurnishingStatuses.map((item: IFurnishingStatus) => (
							<Option key={item.id} value={item.id}>
								{item.name_en}
							</Option>
						))}
					</Select>
				</Form.Item>
				<Form.Item name="completedStatusId" label="Completed status">
					<Select
						style={{ width: '100%' }}
						placeholder="Please select"
						onChange={(value: number) => onChangeNumberField('completedStatusId', value)}>
						{allCompletedStatuses.map((item: IPurpose) => (
							<Option key={item.id} value={item.id}>
								{item.name_en}
							</Option>
						))}
					</Select>
				</Form.Item>
				<Form.Item name="typeId" label="Type">
					<Select
						style={{ width: '100%' }}
						placeholder="Please select"
						onChange={(value: number) => onChangeNumberField('typeId', value)}>
						{allTypes.map((item: IType) => (
							<Option key={item.id} value={item.id}>
								{item.name_en}
							</Option>
						))}
					</Select>
				</Form.Item>
				<Form.Item name="rentFrequencyId" label="Rent frequency">
					<Select
						style={{ width: '100%' }}
						placeholder="Please select"
						onChange={(value: number) => onChangeNumberField('rentFrequencyId', value)}>
						{allRentFrequencies.map((item: IRentFrequency) => (
							<Option key={item.id} value={item.id}>
								{item.name_en}
							</Option>
						))}
					</Select>
				</Form.Item>
				<Form.Item name="complexId" label="Living complex">
					<Select
						style={{ width: '100%' }}
						placeholder="Please select"
						disabled={!isOwner}
						onChange={(value: number) => onChangeNumberField('complexId', value)}>
						{allComplexes.map((item: IComplex) => (
							<Option key={item.id} value={item.id}>
								{item.title}
							</Option>
						))}
					</Select>
				</Form.Item>
				<Form.Item label={`Cover (${lotCover ? 1 : 0}/1)`}>
					{isCoverLoading && <S.Loader />}
					<input
						type="file"
						ref={coverInputRef}
						name="lotCover"
						onChange={onUploadCover}
						style={{ display: 'none' }}
					/>
					<Button
						type="dashed"
						icon={<FileImageOutlined />}
						onClick={onUploadCoverClick}
						style={{ width: '100%' }}
						disabled={!!lotCover}>
						Upload cover
					</Button>
					<S.Images>
						{!!lotCover && (
							<S.Image>
								<S.Remove onClick={onRemoveCover}>
									<CloseOutlined
										style={{
											fontSize: '8px',
											color: '#000',
											width: '8px',
											display: 'block',
										}}
									/>
								</S.Remove>
								<Image
									src={lotCover.url}
									alt={lotCover.fileName}
									width={84}
									height={84}
									style={{ objectFit: 'cover' }}
								/>
							</S.Image>
						)}
					</S.Images>
				</Form.Item>
				<Form.Item label={`Photos (${lotPhotos.length}/10)`}>
					{isPhotosLoading && <S.Loader />}
					<input
						type="file"
						ref={photosInputRef}
						name="lotPhotos"
						onChange={onUploadPhotos}
						style={{ display: 'none' }}
						multiple
						maxLength={10}
					/>
					<Button
						type="dashed"
						icon={<FileImageOutlined />}
						onClick={onUploadPhotosClick}
						style={{ width: '100%' }}
						disabled={lotPhotos.length > 9}>
						Upload photos
					</Button>
					<S.Images>
						{lotPhotos.map((photo: ILotPhoto) => (
							<S.Image key={photo.id}>
								<S.Remove onClick={() => onRemovePhoto(photo.id)}>
									<CloseOutlined
										style={{
											fontSize: '8px',
											color: '#000',
											width: '8px',
											display: 'block',
										}}
									/>
								</S.Remove>
								<Image
									src={photo.url}
									alt={photo.fileName}
									width={84}
									height={84}
									style={{ objectFit: 'cover' }}
								/>
							</S.Image>
						))}
					</S.Images>
				</Form.Item>
				<Form.Item name="consultations" label="Consultations">
					<Select
						mode="multiple"
						style={{ width: '100%' }}
						placeholder="Please select"
						options={consultations.map((item: IConsultation) => ({
							key: item.id,
							value: `userId: ${item.userId}`,
						}))}
						disabled
					/>
				</Form.Item>
				<Form.Item name="favourites" label="Favourites">
					<Select
						mode="multiple"
						style={{ width: '100%' }}
						placeholder="Please select"
						options={favourites.map((item: IFavourite) => ({
							key: item.id,
							value: `userId: ${item.userId}`,
						}))}
						disabled
					/>
				</Form.Item>
			</Form>
		</Drawer>
	);
};

export default EditLotModal;
